import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { isContentSettled, useLiveTick } from '@/lib/liveContent';
import { getForesee, FORESEE_DEMO_HREF, FORESEE_PRICING_HREF, FORESEE_ROUTE } from '@/data/foresee';
import { trackEvent } from '@/lib/analytics';
import { ForeseeHero } from '@/components/site/foresee/ForeseeHero';
import { ForeseeChallenge } from '@/components/site/foresee/ForeseeChallenge';
import { ForeseeWorkflow } from '@/components/site/foresee/ForeseeWorkflow';
import { ForeseeCapabilities } from '@/components/site/foresee/ForeseeCapabilities';
import { ForeseeFrontline } from '@/components/site/foresee/ForeseeFrontline';
import { ForeseeComplianceHealth } from '@/components/site/foresee/ForeseeComplianceHealth';
import { ForeseeOutcomes } from '@/components/site/foresee/ForeseeOutcomes';
import { ForeseeIso, ForeseeTraining, ForeseeEsg } from '@/components/site/foresee/ForeseeCompliance';
import { ForeseeContractors } from '@/components/site/foresee/ForeseeContractors';
import { ForeseeResources } from '@/components/site/foresee/ForeseeResources';
import { ForeseeAi } from '@/components/site/foresee/ForeseeAi';
import { ForeseeSites } from '@/components/site/foresee/ForeseeSites';
import { ForeseeImplementation } from '@/components/site/foresee/ForeseeImplementation';
import { ForeseeIndustries } from '@/components/site/foresee/ForeseeIndustries';
import { ForeseePricing } from '@/components/site/foresee/ForeseePricing';
import { ForeseeFaq } from '@/components/site/foresee/ForeseeFaq';
import { ForeseeRelated } from '@/components/site/foresee/ForeseeRelated';
import { CtaBand } from '@/components/site/CtaBand';

// Public Foresee product-marketing page. All content reads from the
// CMS-backed listing record (cms_listings / amden-foresee); the Foresee
// application itself is separate and untouched.
export default function ForeseePage() {
  useLiveTick();
  const listingsSettled = isContentSettled('listings');
  const listing = getForesee();
  const sp = listing?.systemPage;
  const ready = listing && listing.status === 'published' && sp;

  useSeo({
    title: listing?.seo?.title || 'Foresee | EHSQ, Compliance & Safety Management Platform',
    description:
      listing?.seo?.description ||
      'Connect frontline reporting, actions, ISO compliance, training, contractors and multi-site visibility with the Foresee EHSQ platform.',
    canonical: listing?.seo?.canonical || `https://amden.ie${FORESEE_ROUTE}`,
    ogImage: listing?.seo?.ogImage,
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://amden.ie/' },
            { '@type': 'ListItem', position: 2, name: 'Foresee', item: `https://amden.ie${FORESEE_ROUTE}` },
          ],
        },
        // Verified facts only — no offers, ratings or invented metrics.
        {
          '@type': 'SoftwareApplication',
          name: 'Foresee',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description: sp?.strapline,
          url: `https://amden.ie${FORESEE_ROUTE}`,
          provider: { '@type': 'Organization', name: 'Amden Group', url: 'https://amden.ie' },
        },
      ],
    },
  });

  useEffect(() => {
    if (ready) trackEvent('foresee_page_viewed');
  }, [ready]);

  if (!ready) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 bg-white px-4 text-center" data-testid="foresee-loading">
        {!listingsSettled ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-navy-900" aria-hidden="true" />
            <span className="sr-only">Loading Foresee</span>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Foresee</h1>
            <p className="max-w-md text-sm leading-relaxed text-slate-600">
              This page is currently being updated. To learn about Foresee in the meantime, contact the Amden team.
            </p>
            <Link to="/contact" className="mt-2 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-navy-700">
              Contact Amden Group
            </Link>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <ForeseeHero sp={sp} />
      <ForeseeChallenge sp={sp} />
      <ForeseeWorkflow sp={sp} />
      <ForeseeCapabilities sp={sp} />
      <ForeseeFrontline sp={sp} />
      <ForeseeComplianceHealth sp={sp} />
      <ForeseeOutcomes />
      <ForeseeIso sp={sp} />
      <ForeseeTraining sp={sp} />
      <ForeseeContractors sp={sp} />
      <ForeseeResources sp={sp} />
      <ForeseeEsg sp={sp} />
      <ForeseeAi sp={sp} />
      <ForeseeSites sp={sp} />
      <ForeseeImplementation sp={sp} />
      <ForeseeIndustries sp={sp} />
      <ForeseePricing sp={sp} listing={listing} />
      <ForeseeFaq />
      <ForeseeRelated sp={sp} />
      <CtaBand
        index="19"
        eyebrow="Foresee"
        heading="Ready to move beyond reactive compliance?"
        copy="See how Foresee can connect frontline reporting, accountable actions and management visibility across your organisation."
        primaryLabel="Book a Foresee Demonstration"
        primaryHref={FORESEE_DEMO_HREF}
        secondaryLabel="Request Pricing"
        secondaryHref={FORESEE_PRICING_HREF}
      />
    </>
  );
}
